/**
 * computeTenantSEO — centralizes tenant microsite SEO computation.
 *
 * Given a branding object (from PublicSiteContext / /api/public/site/branding)
 * returns the full set of head-tag values needed:
 *   { favicon, ogImage, logoUrl, localBusinessSchema }
 *
 * The returned `localBusinessSchema` includes NAP (name / address / phone),
 * areaServed from service_areas, sameAs from social_links, and
 * aggregateRating when reviews exist on the tenant — producing a rich
 * LocalBusiness JSON-LD that earns Google Maps / Local Pack placement.
 *
 * Pure function (no React hooks) — safe to call after early returns.
 */
export function computeTenantSEO(branding) {
  if (!branding) {
    return {
      favicon: undefined,
      appleTouchIcon: undefined,
      ogImage: undefined,
      logoUrl: undefined,
      localBusinessSchema: null,
    };
  }

  const favicon = branding.favicon_url || undefined;
  const logoUrl = branding.logo_url || undefined;
  const heroImage = branding.hero_image_url || undefined;
  // Prefer hero image for social shares (more visually rich), fall back to logo
  const ogImage = heroImage || logoUrl || undefined;

  // Normalize service_areas (may be string or array)
  const serviceAreas = (() => {
    const raw = branding.service_areas;
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.map(s => String(s).trim()).filter(Boolean);
    if (typeof raw === "string") return raw.split(",").map(s => s.trim()).filter(Boolean);
    return [];
  })();

  // Normalize social_links { twitter: "...", facebook: "...", ... }
  const sameAs = (() => {
    const sl = branding.social_links || {};
    if (typeof sl !== "object" || Array.isArray(sl)) return [];
    return Object.values(sl).map(v => String(v || "").trim()).filter(Boolean);
  })();

  // aggregate_rating: { rating_value, review_count } from backend
  const ar = branding.aggregate_rating;
  const aggregateRating = ar && ar.rating_value && ar.review_count
    ? {
        "@type": "AggregateRating",
        ratingValue: ar.rating_value,
        reviewCount: ar.review_count,
      }
    : null;

  // Build LocalBusiness schema only when we have at least a business name
  const bizName = branding.business_name;
  const localBusinessSchema = bizName
    ? {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: bizName,
        description: branding.tagline || "Party rental services",
        priceRange: "$$",
        ...(branding.phone && { telephone: branding.phone }),
        ...(branding.email && { email: branding.email }),
        ...(logoUrl && { image: logoUrl, logo: logoUrl }),
        ...((branding.address || branding.city) && {
          address: {
            "@type": "PostalAddress",
            ...(branding.address && { streetAddress: branding.address }),
            ...(branding.city && { addressLocality: branding.city }),
            ...(branding.state && { addressRegion: branding.state }),
            ...(branding.zip_code && { postalCode: branding.zip_code }),
            addressCountry: branding.country || "US",
          },
        }),
        ...(serviceAreas.length && {
          areaServed: serviceAreas.map(a => ({ "@type": "City", name: a })),
        }),
        ...(sameAs.length && { sameAs }),
        ...(aggregateRating && { aggregateRating }),
      }
    : null;

  return {
    favicon,
    appleTouchIcon: favicon, // reuse favicon for apple-touch if no explicit field
    ogImage,
    logoUrl,
    localBusinessSchema,
  };
}
